import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Modal, ModalHeader, ModalBody, Alert, Table } from 'reactstrap';

import { fetchExternalApiLog, statusBadgeClass } from '../helpers/externalApiLogs';
import { authErrorLabel } from '../helpers/externalApiStats';
import { formatMs, formatNumber, formatPercent } from '../helpers/format';
import { formatPoints } from '../helpers/externalApiCost';
import CopyButton from './copyButton';
import { isKnownReplica, replicaLoadHint, REPLICA_THRESHOLDS } from '../helpers/externalApiReplicas';

const DB_QUERIES_PAGE_SIZE = 10;

const styles = {
    kvGrid: { display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 10, columnGap: 12, fontSize: 13 },
    kvKey: { color: '#818FA5', fontWeight: 500 },
    kvValue: { color: '#3A3F56', wordBreak: 'break-word' },
    mono: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12 },
    section: { marginBottom: 22 },
    sectionTitle: { fontSize: 12, fontWeight: 700, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
    json: {
        margin: 0, padding: 10, backgroundColor: '#F4F6F8', border: '1px solid #E6EDF5', borderRadius: 4,
        fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12,
        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 260, overflow: 'auto',
    },
    link: { background: 'transparent', border: 'none', padding: 0, color: '#33405E', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 },
    badge: {
        display: 'inline-block', padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
        backgroundColor: '#E6EDF5', color: '#33405E',
    },
    dbSummaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 14 },
    dbSummaryTile: { backgroundColor: '#F4F6F8', borderRadius: 6, padding: '8px 10px' },
    dbSummaryLabel: { fontSize: 10, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.3 },
    dbSummaryValue: { fontSize: 16, fontWeight: 600, color: '#3A3F56', marginTop: 2 },
    waterfall: { position: 'relative', height: 'auto', marginBottom: 16 },
    waterfallRow: { position: 'relative', height: 20, marginBottom: 3 },
    waterfallTrack: { position: 'absolute', top: 0, left: 0, right: 0, height: '100%', backgroundColor: '#F4F6F8', borderRadius: 3 },
    waterfallBar: { position: 'absolute', top: 2, height: 16, borderRadius: 3, minWidth: 2 },
    mono11: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 11, color: '#818FA5' },
    repeatedRow: { backgroundColor: '#FFF4E0' },
    payloadBox: {
        margin: 0, padding: '6px 8px', backgroundColor: '#F4F6F8', border: '1px solid #E6EDF5', borderRadius: 4,
        fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 11,
        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 140, overflow: 'auto', maxWidth: 260,
    },
    dbPager: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
        marginTop: 10,
    },
    dbPagerBtn: {
        width: 28, height: 28, borderRadius: 6, border: '1px solid #D2D6DA', backgroundColor: '#FFFFFF',
        color: '#33405E', cursor: 'pointer', fontSize: 14, lineHeight: 1,
    },
    dbPagerLabel: { fontSize: 12, color: '#818FA5' },
};

/** Try to pretty-print a payload JSON string (filter/pipeline/update/...); falls back to the raw string when truncation broke the JSON */
function formatPayloadString(str) {
    if (!str) {
        return null;
    }

    try {
        return JSON.stringify(JSON.parse(str), null, 2);
    } catch (e) {
        return str;
    }
}

export function PayloadCell({ lang, query }) {
    let raw = query.filter || query.pipeline || query.update || query.payload || query.key;

    if (!raw) {
        return <span style={{ color: '#818FA5' }}>-</span>;
    }

    let pretty = formatPayloadString(raw);

    return (
        <div>
            <pre style={styles.payloadBox}>{pretty}</pre>
            <div style={{ marginTop: 2 }}>
                <CopyButton lang={lang} value={raw} label="Copy payload" />
                {query.projection ? <span style={{ ...styles.mono11, marginLeft: 6 }}>{'projection'.translate(lang)}: {query.projection}</span> : null}
                {query.sort ? <span style={{ ...styles.mono11, marginLeft: 6 }}>{'sort'.translate(lang)}: {query.sort}</span> : null}
                {query.limit !== undefined && query.limit !== null ? <span style={{ ...styles.mono11, marginLeft: 6 }}>{'limit'.translate(lang)}: {query.limit}</span> : null}
                {query.skip !== undefined && query.skip !== null ? <span style={{ ...styles.mono11, marginLeft: 6 }}>{'skip'.translate(lang)}: {query.skip}</span> : null}
            </div>
        </div>
    );
}

/**
* The request's own DB query timeline, drawn as a waterfall - a bar per
* query positioned at `atMs` and sized to `durationMs`, against the whole
* request's own duration, the way a browser's network tab draws one.
*/
function DbWaterfall({ queries, totalDurationMs, lang }) {
    let total = Number(totalDurationMs) || Math.max(1, ...queries.map((q) => (q.atMs || 0) + (q.durationMs || 0)));

    return (
        <div style={styles.waterfall}>
            {queries.map((q, idx) => {
                let left = Math.max(0, Math.min(100, ((q.atMs || 0) / total) * 100));
                let unfinished = q.durationMs === null || q.durationMs === undefined;
                let widthMs = unfinished ? Math.max(0, total - (q.atMs || 0)) : q.durationMs;
                let width = Math.max(0.5, Math.min(100 - left, (widthMs / total) * 100));
                let slow = !unfinished && Number(q.durationMs) > 100;

                return (
                    <div key={idx} style={styles.waterfallRow} title={`#${q.n} ${q.collection}.${q.op} - ${unfinished ? 'unfinished'.translate(lang) : formatMs(q.durationMs)}`}>
                        <div style={styles.waterfallTrack} />
                        <div
                            style={{
                                ...styles.waterfallBar,
                                left: `${left}%`,
                                width: `${width}%`,
                                backgroundColor: slow ? '#d03b3b' : '#2a78d6',
                                border: unfinished ? '1px dashed #818FA5' : 'none',
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
}

/**
* Which replica (swarm slot) handled the request and how busy it was when
* the request arrived - for a slow request, a hint whether the replica was
* congested or the request is slow on its own.
*/
export function ReplicaState({ log, lang }) {
    let load = log.replicaLoad;
    let hint = replicaLoadHint(log, lang);
    let lagHigh = load && Number(load.eventLoopLagMs) > REPLICA_THRESHOLDS.lagWarningMs;
    let busy = load && Number(load.inFlight) >= REPLICA_THRESHOLDS.busyInFlight;

    return (
        <div style={styles.section}>
            <div style={styles.sectionTitle}>{'Replica state'.translate(lang)}</div>
            {!isKnownReplica(log.replica) && !load ? (
                <span style={{ color: '#818FA5', fontSize: 13 }}>{'No replica data (request made before the replica tracking was deployed).'.translate(lang)}</span>
            ) : (
                <>
                    <div style={styles.kvGrid}>
                        <div style={styles.kvKey}>{'Replica'.translate(lang)}</div>
                        <div style={styles.kvValue}>{isKnownReplica(log.replica) ? log.replica : '—'}</div>

                        <div style={styles.kvKey} title={'Other requests being processed on the replica when this one arrived'.translate(lang)}>{'In flight'.translate(lang)}</div>
                        <div style={{ ...styles.kvValue, fontWeight: busy ? 600 : 400 }}>{load && load.inFlight !== null && load.inFlight !== undefined ? formatNumber(load.inFlight) : '—'}</div>

                        <div style={styles.kvKey} title={'Event loop lag in the last 5 s'.translate(lang)}>{'Lag max / avg'.translate(lang)}</div>
                        <div style={{ ...styles.kvValue, fontWeight: lagHigh ? 600 : 400 }}>
                            {load && load.eventLoopLagMs !== null && load.eventLoopLagMs !== undefined ? formatMs(load.eventLoopLagMs) : '—'}
                            {' / '}
                            {load && load.eventLoopLagMeanMs !== null && load.eventLoopLagMeanMs !== undefined ? formatMs(load.eventLoopLagMeanMs) : '—'}
                        </div>
                    </div>
                    {hint ? (
                        <Alert color={hint.congested ? 'warning' : 'info'} style={{ fontSize: 13, marginTop: 12, marginBottom: 0 }}>
                            {hint.congested ? '▲ ' : ''}{hint.text}
                        </Alert>
                    ) : null}
                </>
            )}
        </div>
    );
}

export function JsonValue({ value, empty }) {
    let hasValue = value !== undefined && value !== null && !(typeof value === 'object' && Object.keys(value).length === 0);

    if (!hasValue) {
        return <span style={{ color: '#818FA5' }}>{empty}</span>;
    }

    return <pre style={styles.json}>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>;
}

/**
* A single external API request log (VD-725 follow-up). Used both from the
* logs table (row click) and from the analytics "Insights" tables (a slow
* request, or the "Example" button on a top error).
*/
function ExternalApiLogDetail({ lang, logId, onClose, onOpenLogs }) {
    let [log, setLog] = useState(null);
    let [loading, setLoading] = useState(false);
    let [error, setError] = useState(null);
    let [dbPage, setDbPage] = useState(0);

    useEffect(() => {
        if (!logId) {
            setLog(null);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        setDbPage(0);

        fetchExternalApiLog(logId).then(({ data, error: err }) => {
            setLoading(false);
            if (err) {
                setError(err);
            } else {
                setLog(data);
            }
        });
    }, [logId]);

    // same collection+op+payload more than once in this one request = a likely N+1
    let repeatedCounts = useMemo(() => {
        let counts = {};
        (log && log.dbQueries ? log.dbQueries : []).forEach((q) => {
            let key = `${q.collection}|${q.op}|${q.filter || q.pipeline || q.update || q.payload || q.key || ''}`;
            counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [log]);

    let repeatedCountFor = (q) => repeatedCounts[`${q.collection}|${q.op}|${q.filter || q.pipeline || q.update || q.payload || q.key || ''}`] || 1;

    let dbQueriesTotal = log && log.dbQueries ? log.dbQueries.length : 0;
    let dbPageCount = Math.max(1, Math.ceil(dbQueriesTotal / DB_QUERIES_PAGE_SIZE));
    let pagedDbQueries = log && log.dbQueries
        ? log.dbQueries.slice(dbPage * DB_QUERIES_PAGE_SIZE, (dbPage + 1) * DB_QUERIES_PAGE_SIZE)
        : [];

    return (
        <Modal isOpen={!!logId} toggle={onClose} size="lg" scrollable className="log-detail-dialog">
            <ModalHeader toggle={onClose}>{'Request log'.translate(lang)}</ModalHeader>
            <ModalBody>
                {loading ? <div style={{ color: '#818FA5' }}>{'Loading...'.translate(lang)}</div> : null}
                {error ? <Alert color="danger">{error}</Alert> : null}

                {!loading && log ? (
                    <>
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>{'Request'.translate(lang)}</div>
                            <div style={styles.kvGrid}>
                                <div style={styles.kvKey}>{'Time'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.createdAt ? moment(log.createdAt).format('YYYY-MM-DD HH:mm:ss.SSS') : '-'}</div>

                                <div style={styles.kvKey}>{'Method'.translate(lang)}</div>
                                <div style={{ ...styles.kvValue, ...styles.mono }}>{log.method}</div>

                                <div style={styles.kvKey}>{'Path'.translate(lang)}</div>
                                <div style={{ ...styles.kvValue, ...styles.mono }}>{log.path || log.route}</div>

                                <div style={styles.kvKey}>{'Status'.translate(lang)}</div>
                                <div style={styles.kvValue}><span className={statusBadgeClass(log.status)}>{log.status}</span></div>

                                <div style={styles.kvKey}>{'Duration'.translate(lang)}</div>
                                <div style={styles.kvValue}>{formatMs(log.durationMs)}</div>

                                <div style={styles.kvKey}>{'Points'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.costPoints !== undefined && log.costPoints !== null ? formatPoints(log.costPoints) : '-'}</div>

                                <div style={styles.kvKey}>{'Client'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.client ? log.client.username : '-'}</div>

                                <div style={styles.kvKey}>{'API key'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.apiKey ? `${log.apiKey.name} (${log.apiKey.prefix})` : '-'}</div>

                                <div style={styles.kvKey}>{'Auth method'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.authMethod || '-'}</div>

                                <div style={styles.kvKey}>{'Auth error'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.authError ? authErrorLabel(log.authError) : '-'}</div>

                                <div style={styles.kvKey}>{'IP'.translate(lang)}</div>
                                <div style={{ ...styles.kvValue, ...styles.mono }}>{log.ip || '-'}</div>

                                <div style={styles.kvKey}>{'User agent'.translate(lang)}</div>
                                <div style={styles.kvValue}>{log.userAgent || '-'}</div>

                                <div style={styles.kvKey}>{'Request ID'.translate(lang)}</div>
                                <div style={{ ...styles.kvValue, ...styles.mono }}>
                                    {log.requestId ? (
                                        <>
                                            {log.requestId}
                                            <span style={{ ...styles.badge, marginLeft: 8 }}>{log.requestIdSource || '-'}</span>
                                            <CopyButton lang={lang} value={log.requestId} label="Copy request ID" />
                                        </>
                                    ) : '-'}
                                </div>

                                <div style={styles.kvKey}>{'Body'.translate(lang)}</div>
                                <div style={styles.kvValue}>
                                    {'Body is not stored.'.translate(lang)}
                                    {log.bodyHash ? (
                                        <>
                                            {' '}
                                            <button style={styles.link} onClick={() => onOpenLogs({ bodyHash: log.bodyHash })}>
                                                {'Same body'.translate(lang)}
                                            </button>
                                        </>
                                    ) : null}
                                </div>

                                <div style={styles.kvKey}>{'Fingerprint'.translate(lang)}</div>
                                <div style={styles.kvValue}>
                                    {log.fingerprint ? (
                                        <button style={styles.link} onClick={() => onOpenLogs({ fingerprint: log.fingerprint })}>
                                            {'All identical requests'.translate(lang)}
                                        </button>
                                    ) : '-'}
                                </div>
                            </div>
                        </div>

                        <ReplicaState log={log} lang={lang} />

                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>{'Params'.translate(lang)}</div>
                            <JsonValue value={log.params} empty="-" />
                        </div>

                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>{'Query'.translate(lang)}</div>
                            <JsonValue value={log.query} empty="-" />
                        </div>

                        {log.error ? (
                            <div style={styles.section}>
                                <div style={styles.sectionTitle}>{'Error'.translate(lang)}</div>
                                <JsonValue value={log.error} empty="-" />
                            </div>
                        ) : null}

                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>{'Database queries'.translate(lang)}</div>

                            {!log.db ? (
                                <span style={{ color: '#818FA5', fontSize: 13 }}>-</span>
                            ) : (
                                <>
                                    <div style={styles.dbSummaryGrid}>
                                        <div style={styles.dbSummaryTile}>
                                            <div style={styles.dbSummaryLabel}>{'Queries'.translate(lang)}</div>
                                            <div style={styles.dbSummaryValue}>{formatNumber(log.db.queries)}</div>
                                        </div>
                                        <div style={styles.dbSummaryTile}>
                                            <div style={styles.dbSummaryLabel}>{'Reads / writes'.translate(lang)}</div>
                                            <div style={styles.dbSummaryValue}>{formatNumber(log.db.reads)} / {formatNumber(log.db.writes)}</div>
                                        </div>
                                        <div style={styles.dbSummaryTile}>
                                            <div style={styles.dbSummaryLabel}>{'Total DB time'.translate(lang)}</div>
                                            <div style={styles.dbSummaryValue}>
                                                {formatMs(log.db.totalMs)}
                                                {log.durationMs ? <span style={{ fontSize: 11, color: '#818FA5', fontWeight: 400 }}> ({formatPercent(log.db.totalMs, log.durationMs)})</span> : null}
                                            </div>
                                        </div>
                                        <div style={styles.dbSummaryTile}>
                                            <div style={styles.dbSummaryLabel}>{'Docs returned'.translate(lang)}</div>
                                            <div style={styles.dbSummaryValue}>{formatNumber(log.db.docsReturned)}</div>
                                        </div>
                                        <div style={styles.dbSummaryTile}>
                                            <div style={styles.dbSummaryLabel}>{'Slowest query'.translate(lang)}</div>
                                            <div style={styles.dbSummaryValue}>{formatMs(log.db.slowestMs)}</div>
                                        </div>
                                        {log.db.unfinished ? (
                                            <div style={styles.dbSummaryTile}>
                                                <div style={styles.dbSummaryLabel}>{'Unfinished'.translate(lang)}</div>
                                                <div style={styles.dbSummaryValue}>{formatNumber(log.db.unfinished)}</div>
                                            </div>
                                        ) : null}
                                    </div>

                                    {log.db.byCollection && log.db.byCollection.length ? (
                                        <div style={{ marginBottom: 14 }}>
                                            {log.db.byCollection.map((c, idx) => (
                                                <span key={idx} style={{ ...styles.badge, marginRight: 6, marginBottom: 6, backgroundColor: '#F4F6F8' }}>
                                                    {c.collection}: {formatNumber(c.queries)} ({formatMs(c.totalMs)})
                                                </span>
                                            ))}
                                        </div>
                                    ) : null}

                                    {log.dbQueries === null || log.dbQueries === undefined ? (
                                        <Alert color="info" style={{ fontSize: 13 }}>
                                            {'Query details are not available (kept for 14 days).'.translate(lang)}
                                        </Alert>
                                    ) : (
                                        <>
                                            {log.db.opsTruncated ? (
                                                <Alert color="warning" style={{ fontSize: 13 }}>
                                                    {`${'Showing the first 200 of'.translate(lang)} ${formatNumber(log.db.queries)} ${'queries'.translate(lang)}.`}
                                                </Alert>
                                            ) : null}

                                            {log.dbQueries.length ? (
                                                <>
                                                    <DbWaterfall queries={pagedDbQueries} totalDurationMs={log.durationMs} lang={lang} />

                                                    <div style={{ overflowX: 'auto' }}>
                                                        <Table size="sm" responsive hover style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>{'Collection'.translate(lang)}</th>
                                                                    <th>{'Op'.translate(lang)}</th>
                                                                    <th>{'Duration'.translate(lang)}</th>
                                                                    <th>{'Docs'.translate(lang)}</th>
                                                                    <th>{'Payload'.translate(lang)}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {pagedDbQueries.map((q, idx) => {
                                                                    let repeated = repeatedCountFor(q);
                                                                    let unfinished = q.durationMs === null || q.durationMs === undefined;
                                                                    return (
                                                                        <tr key={idx} style={repeated > 1 ? styles.repeatedRow : undefined}>
                                                                            <td>{q.n}</td>
                                                                            <td style={styles.mono}>
                                                                                {q.collection}
                                                                                {repeated > 1 ? <div style={styles.badge}>{`${'repeated'.translate(lang)} ${repeated}×`}</div> : null}
                                                                            </td>
                                                                            <td style={styles.mono}>{q.op}</td>
                                                                            <td style={{ color: !unfinished && Number(q.durationMs) > 100 ? '#d03b3b' : '#3A3F56', fontWeight: !unfinished && Number(q.durationMs) > 100 ? 600 : 400 }}>
                                                                                {unfinished ? 'unfinished'.translate(lang) : formatMs(q.durationMs)}
                                                                            </td>
                                                                            <td>
                                                                                {q.docs !== undefined && q.docs !== null ? formatNumber(q.docs)
                                                                                    : q.count !== undefined && q.count !== null ? formatNumber(q.count)
                                                                                        : q.result ? JSON.stringify(q.result)
                                                                                            : q.documents !== undefined ? formatNumber(q.documents)
                                                                                                : '-'}
                                                                            </td>
                                                                            <td><PayloadCell lang={lang} query={q} /></td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                    </div>

                                                    {dbPageCount > 1 ? (
                                                        <div style={styles.dbPager}>
                                                            <button
                                                                type="button"
                                                                style={{ ...styles.dbPagerBtn, ...(dbPage === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : null) }}
                                                                disabled={dbPage === 0}
                                                                onClick={() => setDbPage((p) => Math.max(0, p - 1))}
                                                            >
                                                                {'<'}
                                                            </button>
                                                            <span style={styles.dbPagerLabel}>
                                                                {`${'Page'.translate(lang)} ${dbPage + 1} / ${dbPageCount}`}
                                                                {' · '}
                                                                {`${dbPage * DB_QUERIES_PAGE_SIZE + 1}-${Math.min(dbQueriesTotal, (dbPage + 1) * DB_QUERIES_PAGE_SIZE)} ${'of'.translate(lang)} ${dbQueriesTotal}`}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                style={{ ...styles.dbPagerBtn, ...(dbPage >= dbPageCount - 1 ? { opacity: 0.4, cursor: 'not-allowed' } : null) }}
                                                                disabled={dbPage >= dbPageCount - 1}
                                                                onClick={() => setDbPage((p) => Math.min(dbPageCount - 1, p + 1))}
                                                            >
                                                                {'>'}
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>{'Headers'.translate(lang)}</div>
                            <JsonValue value={log.headers} empty="-" />
                        </div>
                    </>
                ) : null}
            </ModalBody>
        </Modal>
    );
}

export default ExternalApiLogDetail;
